<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

// Points to the afters.me app, environment-aware (stag1 on staging, app on prod).
const APP_BASE_URL =
  window.chatwootConfig?.aftersAppURL || 'https://app.afters.me';

const { t } = useI18n();
const globalConfig = useMapGetter('globalConfig/get');
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');

const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');

// The `inbox` tab is Chatwoot itself, so it's always the active tab in this nav.
const mainItems = computed(() => [
  { id: 'appointments', label: t('AFTERS_NAV.APPOINTMENTS'), href: `${APP_BASE_URL}/` },
  { id: 'clients', label: t('AFTERS_NAV.CLIENTS'), href: `${APP_BASE_URL}/patients/` },
  { id: 'campaigns', label: t('AFTERS_NAV.CAMPAIGNS'), href: `${APP_BASE_URL}/audiences/` },
  {
    id: 'inbox',
    label: t('AFTERS_NAV.INBOX'),
    href: `/app/accounts/${accountId.value}/dashboard`,
    active: true,
  },
  ...(isSuperAdmin.value
    ? [{ id: 'strategy', label: t('AFTERS_NAV.STRATEGY'), href: `${APP_BASE_URL}/strategy/` }]
    : []),
]);

const settingsItem = computed(() => ({
  id: 'settings',
  label: t('AFTERS_NAV.SETTINGS'),
  href: `${APP_BASE_URL}/settings/`,
}));
</script>

<template>
  <header
    class="flex items-center flex-shrink-0 h-14 gap-6 px-6 bg-white border-b border-n-weak"
  >
    <a :href="`${APP_BASE_URL}/`" class="flex items-center flex-shrink-0">
      <img
        :src="globalConfig.logo"
        alt="Afters"
        class="w-auto h-6"
      />
    </a>

    <nav class="flex items-center gap-1 ml-2">
      <a
        v-for="item in mainItems"
        :key="item.id"
        :href="item.href"
        class="flex items-center px-3 py-1.5 text-sm rounded-md transition-colors"
        :class="
          item.active
            ? 'bg-n-brand/10 text-n-blue-11 font-medium'
            : 'font-normal text-n-slate-11 hover:bg-n-alpha-2'
        "
      >
        {{ item.label }}
      </a>
    </nav>

    <a
      :href="settingsItem.href"
      class="flex items-center gap-1.5 px-3 py-1.5 ml-auto text-sm font-normal rounded-md text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
    >
      <span class="i-lucide-settings size-4" />
      {{ settingsItem.label }}
    </a>
  </header>
</template>
